import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { ENTITY_TYPES } from '@sales-center/common'

import { useGetEntityFormDataOnMount } from '@user/components/Settings/EntityFormSettings/hooks'
import { selectAllBranches } from '@user/store/branches'
import { selectAllCampaigns } from '@user/store/campaigns'
import { selectAllCustomFields } from '@user/store/customFields'
import type { DraftLead } from '@user/store/draftLeads/typings'
import { selectAllDivisions } from '@user/store/divisions'
import { selectAllLocationTypes } from '@user/store/locationTypes'
import { selectAllSources } from '@user/store/sources'
import { selectAllTargetPests } from '@user/store/targetPests'

import type { AutoReviewLookups } from '../typings'
import { groupMissingFields } from '../utils/groupMissingFields'
import {
  getEditableMissingFields,
  getNonEditableMissingFields,
} from '../utils/nonEditableMissingFields'
import { useLoadLookups } from './useLoadLookups'

/**
 * Loads the store data the modal's inputs read, only for the fields reported missing, and
 * derives, from the lead's server-reported missing fields, what to render, validate and send.
 * Only editable fields are validated and sent; referral and unknown keys are shown as notes.
 */
export function useData(lead: DraftLead) {
  useGetEntityFormDataOnMount(ENTITY_TYPES.Lead)

  const branches = useSelector(selectAllBranches)
  const divisions = useSelector(selectAllDivisions)
  const locationTypes = useSelector(selectAllLocationTypes)
  const campaigns = useSelector(selectAllCampaigns)
  const sources = useSelector(selectAllSources)
  const customFields = useSelector(selectAllCustomFields)
  const targetPests = useSelector(selectAllTargetPests)

  const lookups = useMemo<AutoReviewLookups>(
    () => ({
      branches: branches.map(({ id, name }) => ({ id, name })),
      divisions: divisions.map(({ id, name }) => ({ id, name })),
      locationTypes: locationTypes.map(({ id, name }) => ({ id, name })),
      campaigns: campaigns.map(({ id, name }) => ({ id: String(id), name })),
      sources: sources.map(({ id, name }) => ({ id, name })),
      customFields,
    }),
    [branches, divisions, locationTypes, campaigns, sources, customFields],
  )

  const missing = useMemo(
    () => lead.missingAutoReviewFields ?? [],
    [lead.missingAutoReviewFields],
  )
  const editableMissing = useMemo(
    () => getEditableMissingFields(missing),
    [missing],
  )
  const nonEditableMissing = useMemo(
    () => getNonEditableMissingFields(missing),
    [missing],
  )
  const groups = useMemo(() => groupMissingFields(missing), [missing])
  const loading = useLoadLookups(groups)

  return {
    loading,
    groups,
    editableMissing,
    nonEditableMissing,
    lookups,
    customFields,
    targetPests,
  } as const
}

export type UseData = ReturnType<typeof useData>
